import { useState, useEffect } from 'react';
import verifyPassword from '@mysimpledb/shared/data/verifyPassword';
import updatePassword from '@mysimpledb/shared/data/updatePassword';
import axios from '@mysimpledb/shared/config/axios';
import { useNotify } from '@mysimpledb/shared/context/NotificationContext';

export default function useSecurityData({
  token,
  email = null,
  resetToken = null,
  onSuccess,
}) {
  const notify = useNotify();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('');

  const criteria = {
    length: (pwd) => pwd.length >= 8,
    upper: (pwd) => /[A-Z]/.test(pwd),
    lower: (pwd) => /[a-z]/.test(pwd),
    digit: (pwd) => /[0-9]/.test(pwd),
    symbol: (pwd) => /[^A-Za-z0-9]/.test(pwd),
  };

  const allCriteriaMet = Object.values(criteria).every((fn) => fn(newPassword));

  useEffect(() => {
    const e = {};

    if (newPassword && !criteria.length(newPassword)) e.length = true;
    if (newPassword && !criteria.upper(newPassword)) e.upper = true;
    if (newPassword && !criteria.lower(newPassword)) e.lower = true;
    if (newPassword && !criteria.digit(newPassword)) e.digit = true;
    if (newPassword && !criteria.symbol(newPassword)) e.symbol = true;

    if (newPassword && confirmPassword && newPassword !== confirmPassword)
      e.match = true;

    setErrors(e);
    setStatus('');
  }, [newPassword, confirmPassword]);

  const validateCurrentPassword = async () => {
    if (!currentPassword) return;

    try {
      const verified = await verifyPassword(token, currentPassword);
      if (!verified) {
        setErrors((prev) => ({ ...prev, current: true }));
      } else {
        setErrors((prev) => {
          const { current, ...rest } = prev;
          return rest;
        });
      }
    } catch {
      setErrors((prev) => ({ ...prev, current: true }));
    }
  };

  const currentPasswordInput = () => {
    setErrors((prev) => ({ ...prev, current: false }));
  };

  const handleSubmit = async () => {
    if (Object.keys(errors).length || newPassword !== confirmPassword) return;

    try {
      if (email && resetToken) {
        // Reset password via email link
        await axios.post('/password/reset-password', {
          email,
          token: resetToken,
          password: newPassword,
        });
      } else {
        // In-account change
        await updatePassword(token, newPassword);
      }

      setStatus('Password updated successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setErrors({});
      notify.show('Password has been changed');
      onSuccess?.();
    } catch {
      setErrors((prev) => ({ ...prev, current: true }));
    }
  };

  return {
    currentPassword,
    newPassword,
    confirmPassword,
    setCurrentPassword,
    setNewPassword,
    setConfirmPassword,
    criteria,
    errors,
    status,
    allCriteriaMet,
    handleSubmit,
    validateCurrentPassword,
    currentPasswordInput,
  };
}
